import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { FileModel } from '../../../shared/model/file.model';
import { GridSettings } from '../../../shared/model/grid-settings.interface';
import { RestApiModel } from '../../../shared/model/rest-api.model';
import { VixxoApiService } from '../../../shared/services/vixxo-api.service';
import { SortUtils } from '../../../shared/utils/sort.utils';
import { AssetHelper } from '../asset.helper';
import { AssetAttachmentModel } from '../components/model/asset-attachment.model';
import { AssetSearchStoredInfoModel } from '../components/model/asset-search-stored-info.model';
import { AssetSearchModel } from '../components/model/asset-search.model';
import { AssetTypeModel } from '../components/model/asset-type.model';
import { AssetModel } from '../components/model/asset.model';

export const MAX_SUPPORTED_SITES_BY_DROPDOWN = 1000;
export const STARBUCKS_SITE_ID = '1-CNYEF';

@Injectable()
export class AssetService {
  private readonly assetListURL = RestApiModel.PROXY_API_URL + '/assetService/assets';
  private readonly assetURL = RestApiModel.PROXY_API_URL + '/asset/';
  private readonly siteURL = RestApiModel.PROXY_API_URL + '/site/';
  private readonly sitesEAPIURL = RestApiModel.PROXY_API_URL + '/sites';
  private readonly makeURL = RestApiModel.PROXY_API_URL + '/assetService/make/';
  private readonly customersURL = RestApiModel.PROXY_API_URL + '/customers';
  private readonly fileUploadURL = RestApiModel.PROXY_API_URL + '/file';
  private readonly assetServiceURL =
    RestApiModel.PROXY_API_URL + '/assetService/asset/';
  private readonly assetAttachment = '/attachment';
  private readonly assetAttachments = '/attachments';

  private assetSearchInput: AssetSearchModel;
  private assetSearchStoredFilters: AssetSearchStoredInfoModel;
  private gridStatePersisting: any;
  public static ASSET_SEARCH_KEY = '_assetSearch';
  public static ASSET_SEARCH_GRID_KEY = '_assetSearchGrid';

  // Resolve HTTP using the constructor
  constructor(
    private http: HttpClient,
    private vixxoApiService: VixxoApiService
  ) {}

  /**
   * Resolves the given error as JSON object
   */
  private handleError(error: any) {
    console.error(error);
    return error;
  }

  /**
   * Execute the Search Based on the Parameters from the Filter
   */
  searchAssets(
    assetSearchModel: AssetSearchModel
  ): Observable<AssetSearchModel> {
    let queryString = AssetHelper.getAssetSearchQueryString(assetSearchModel);
    return this.http
      .get(this.assetListURL + queryString)
      .map((res: any) => {
        return res;
      })
      .catch(err => {
        return throwError(err);
      });
  }

  searchAssetID(assetId: String): Observable<any> {
    return this.http
      .get(this.assetServiceURL + assetId)
      .map((res: any) => {
        return res;
      })
      .catch(err => {
        return throwError(err);
      });
  }

  /**
   * Gets a list of customers.
   * @returns {Observable<any | any>}
   */
  getCustomers(): Observable<any> {
    return this.http
      .get(this.customersURL)
      .map((res: any) => {
        return res.results ? res.results : null;
      })
      .catch(this.handleError);
  }

  /**
   *
   * @param {number} id
   * @returns {Observable<any>}
   */
  getAssetById(id: number): Observable<any> {
    return this.http
      .get(this.assetServiceURL + id)
      .map((res: any) => {
        return res ? res : null;
      })
      .catch(this.handleError);
  }

  /**
   * Gets the product info based on the Product Id found in the current asset
   * @param {number} assetId
   * @returns {Observable<any>}
   */
  getAssetProduct(productId: number): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/product/' + productId)
      .map((res: any) => {
        return res.results ? res.results : null;
      })
      .catch(this.handleError);
  }

  /**
   * GET - Site by Id.
   * @param {string} id
   * @returns {Observable<any>}
   */
  getSiteById(id: string): Observable<any> {
    return this.http
      .get(this.siteURL + id)
      .map((res: any) => {
        return res.results ? res.results : null;
      })
      .catch(this.handleError);
  }

  /**
   * GET - Make by id.
   * @param {string} endpoint
   * @returns {Observable<any>}
   */
  getMakeById(id: number): Observable<any> {
    return this.http
      .get(this.makeURL + id)
      .map((res: any) => {
        return res ? res : null;
      })
      .catch(this.handleError);
  }

  /**
   * Gets a list of sites, based on the given Customer Id.
   * @param {string} customerID
   * @returns {Observable<any>}
   */
  getSites(customerID: string, params?: any): Observable<any> {
    let queryString = `?customerNumber=${customerID}&pageSize=1000`;

    if (params.filterValue) {
      queryString += `&searchString=${params.filterValue}`;
    }

    if (params.siteId) {
      queryString += `&siteId=${params.siteId}`;
    }

    return this.http
      .get(this.sitesEAPIURL + queryString)
      .map((res: any) => {
        let jsonResponse: any = res;
        let results: Array<any> =
          jsonResponse.results || jsonResponse.result.results;
        results.forEach(site => {
          if (!site.name) {
            site.name = '';
          }
          if (site.name.indexOf(site.number) > -1) {
            site.name = `${site.name.substring(
              0,
              site.name.indexOf(site.number)
            )} (${site.number})`;
          } else {
            site.name = `${site.name} (${site.number})`;
          }
        });
        return results;
      })
      .catch(this.handleError);
  }

  /**
   * GET, retrieves the asset types associated to the product.
   * via Asset <-> Product
   * @deprecated Endpoint no longer exits, please use: getAssetType() by id method.
   * @param productId
   * @returns {Observable<any>}
   */
  getAssetTypesForProduct(productId): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/product/${productId}/assetTypes`)
      .map((res: any) => {
        return res.results ? res.results : null;
      })
      .catch(this.handleError);
  }

  /**
   * GET - Obtain the asset type by id.
   * @param {number} assetTypeId
   * @returns {Observable<AssetTypeModel>}
   */
  getAssetType(assetTypeId: number): Observable<AssetTypeModel> {
    assetTypeId = assetTypeId ? assetTypeId : 0;
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/assetType/${assetTypeId}`)
      .map((res: any) => {
        return res.results ? res.results : null;
      })
      .catch(this.handleError);
  }

  /**
   * GET - Obtain the list of asset types
   * @returns {Observable<Array<AssetTypeModel>>}
   */
  getAssetTypes(): Observable<Array<AssetTypeModel>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/assetTypes`)
      .map((res: any) => {
        if (res.results) {
          return SortUtils.sortCollection(res.results, 'name');
        } else {
          return [];
        }
      })
      .catch(error => Observable.of(error));
  }

  /**
   * GET asset card view data.
   * @param row
   * @returns {Observable<any>}
   */
  /*public getAssetDetailCardView(row: any): Observable<any> {
		return Observable.create(observer => {
			setTimeout(() => {
				let response = new Response( new ResponseOptions({
					body: {
						data: 'test'
					}
				}));
				observer.next(response);
				observer.complete();
			}, 100);
		});
	}*/

  /**
   * PUT - Updates an asset.
   */
  updateAsset(assetModel: AssetModel): Observable<any> {
    if (assetModel['createdDate']) {
      delete assetModel['createdDate'];
    }
    this.setYearForManufacturedDate(assetModel);
    const payload = { ...assetModel };
    this.removeAssetUnnecessaryPayload(payload);
    return this.http
      .put(this.assetServiceURL + assetModel.id, payload as any)
      .map((res: any) => {
        return res;
      })
      .catch((error: any) => Observable.of(error));
  }

  private removeAssetUnnecessaryPayload(payload: any): void {
    delete payload.site;
    delete payload.make;
    delete payload.product;
    delete payload.assetType;
    if (payload.tag === '') {
      delete payload.tag;
    }
    if (payload.assetLocation === '') {
      delete payload.assetLocation;
    }
  }

  /**
   * POST - Creates an asset.
   */
  createAsset(assetModel: AssetModel): Observable<any> {
    this.setYearForManufacturedDate(assetModel);
    const payload = { ...assetModel };
    this.removeAssetUnnecessaryPayload(payload);
    return this.http
      .post(this.assetURL, payload as any)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Sets the correct date based on the year for the manufacturedDate
   * @param {AssetModel} assetModel
   */
  private setYearForManufacturedDate(assetModel: AssetModel): void {
    assetModel.manufacturedDate = assetModel.manufacturedDate
      ? new Date(
          new Date(assetModel.manufacturedDate).getFullYear(),
          1,
          1,
          1,
          0,
          0
        )
      : null;
  }

  /**
   * Deletes the assets located in the given array of assets.
   * @param {AssetModel[]} assets
   * @returns {Observable<any[]>}
   */
  deleteListOfAssets(assets: AssetModel[]): Observable<any> {
    let assetProcess = [];
    assets.forEach((asset: any) => {
      assetProcess.push(this.deleteAsset(asset.id));
    });
    return Observable.forkJoin(assetProcess);
  }

  /**
   * Deletes an asset, based on the given Asset Id.
   * @param {string} assetId
   * @returns {Observable<any>}
   */
  deleteAsset(assetId: number): Observable<any> {
    return this.http
      .delete(this.assetURL + assetId)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Activates the assets located in the given array of assets.
   * @param {AssetModel[]} assets
   * @returns {Observable<any[]>}
   */
  activateListOfAssets(assets: AssetModel[]): Observable<any> {
    let assetProcess = [];
    assets.forEach((asset: any) => {
      assetProcess.push(this.activateAsset(asset));
    });
    return Observable.forkJoin(assetProcess);
  }

  /**
   * Activates an asset.
   * @param {string} assetId
   * @returns {Observable<any>}
   */
  activateAsset(asset: AssetModel): Observable<any> {
    asset.isActive = true;
    return this.updateAsset(asset);
  }

  /**
   * Update the product id to a set of assets.
   * @param {Array<AssetModel>} assets
   * @param {number} productId
   */
  updateAssets(assets: Array<AssetModel>, productId: number): Observable<any> {
    const observables = [];
    assets.forEach(element => {
      element.productId = productId;
      observables.push(this.updateAsset(element));
    });
    return Observable.forkJoin(observables).map(res => res);
  }

  /**
   * Set the model to store asset search filters.
   * @param input
   */
  setAssetSearchStoredInfo(
    assetSearchStoredFilters: AssetSearchStoredInfoModel
  ) {
    this.assetSearchStoredFilters = assetSearchStoredFilters;
  }

  /**
   * Returns the stored asset search filters.
   */
  getAssetSearchStoredInfo(): AssetSearchStoredInfoModel {
    if (this.assetSearchStoredFilters) {
      return this.assetSearchStoredFilters;
    } else {
      return new AssetSearchStoredInfoModel();
    }
  }

  /**
   * Set the model to store asset search filters.
   * @param input
   */
  setAssetSearchInput(input: AssetSearchModel) {
    localStorage.setItem(AssetService.ASSET_SEARCH_KEY, JSON.stringify(input));
  }
  /**
   * Returns the stored asset search filters.
   */
  getAssetSearchInput(): AssetSearchModel {
    if (localStorage.getItem(AssetService.ASSET_SEARCH_KEY)) {
      return JSON.parse(localStorage.getItem(AssetService.ASSET_SEARCH_KEY));
    } else {
      return new AssetSearchModel();
    }
  }
  /**
   * setter and getter for grid column-state persist value
   */
  setStatePersistingInputs(input: GridSettings) {
    localStorage.setItem(
      AssetService.ASSET_SEARCH_GRID_KEY,
      JSON.stringify(input)
    );
  }
  /**
   * setter and getter for grid column-state persist value
   */
  getStatePersistingInputs() {
    if (localStorage.getItem(AssetService.ASSET_SEARCH_GRID_KEY)) {
      return JSON.parse(
        localStorage.getItem(AssetService.ASSET_SEARCH_GRID_KEY)
      );
    } else {
      return null;
    }
  }

  postFileUpload(file: FileModel) {
    return this.vixxoApiService.postRequest(this.fileUploadURL, file);
  }

  postAssetAttachment(
    assetId: number,
    assetAttachmentModel: AssetAttachmentModel
  ) {
    return this.http.post(
      this.assetServiceURL + assetId + this.assetAttachment,
      assetAttachmentModel
    );
  }

  getAssetAttachments(assetId: number) {
    return this.http.get(
      this.assetServiceURL + assetId + this.assetAttachments
    );
  }
}
